/**
 * Message Builder Utilities
 *
 * Helper functions for creating chat messages with proper structure.
 * These builders ensure consistency across the codebase when creating
 * user messages, AI placeholders, and other message types.
 */

import type { ChatMessage, DeepResearchContext } from '../../types';
import { currentDate, now } from '@/shared/utils/dateUtils';

/**
 * Parameters for building an AI message placeholder
 */
export interface BuildAiPlaceholderParams {
  aiMessageId: string;
  userMessageId: string;
  aiAssistantName?: string;
}

/**
 * Creates an AI message placeholder that will be updated with streaming content.
 * This is used when initiating a chat request to show the AI is processing.
 *
 * @param params - The parameters for creating the placeholder
 * @returns A ChatMessage object with 'sending' status ready for streaming
 */
export const buildAiPlaceholder = ({
  aiMessageId,
  userMessageId,
  aiAssistantName,
}: BuildAiPlaceholderParams): ChatMessage => ({
  id: aiMessageId,
  text: '',
  author: 'ai',
  timestamp: currentDate(),
  status: 'sending',
  responseTo: userMessageId,
  assistantName: aiAssistantName,
});

/**
 * Creates an initial deep research context for AI messages.
 * This context tracks the state and progress of deep research operations.
 *
 * @returns A DeepResearchContext object in the initial ACTIVE state
 */
export const buildInitialDeepResearchContext = (): DeepResearchContext => ({
  state: 'ACTIVE',
  currentStep: {
    id: `init-${now()}`,
    node: 'init',
    title: 'Starting Deep Research...',
    description: 'Initializing research environment...',
    timestamp: currentDate().toISOString(),
  },
  history: [],
  stats: {
    durationMs: 0,
    sourceCount: 0,
    searchCount: 0,
  },
  startTimestamp: now(),
});

/**
 * Parameters for building a user message
 */
export interface BuildUserMessageParams {
  messageId: string;
  text: string;
  attachments?: import('../../types').FileAttachment[];
}

/**
 * Creates a user message with the proper structure.
 *
 * @param params - The parameters for creating the user message
 * @returns A ChatMessage object representing the user's message
 */
export const buildUserMessage = ({
  messageId,
  text,
  attachments,
}: BuildUserMessageParams): ChatMessage => ({
  id: messageId,
  text,
  author: 'user',
  timestamp: currentDate(),
  status: 'sent',
  attachments: attachments || [],
});
